import {
    IconSpeakerphone,
    IconCalendarEvent,
    IconBooks,
    IconUser,
    IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits, formatBnDate, jobLogoSrc } from '../../../utils/bnFormat';
import usePosterFit from './usePosterFit';
import { posterTextOrNA } from './posterParts';

/**
 * LandscapeFit — a fixed A4-landscape (297:210) sheet. The body fills the full
 * width (no side letterboxing); usePosterFit tunes `--fs` so the content fills
 * the sheet height, and the footer pins to the bottom. Decorative glow + double
 * frame sit on the full box (their cqw units resolve against this container).
 */
function LandscapeFit({ bg, decor, frame, className, boxRef, innerRef, children }) {
    return (
        <div
            ref={boxRef}
            className={`relative overflow-hidden ${className}`}
            style={{ aspectRatio: '297 / 210', background: bg, containerType: 'inline-size', '--fs': 1 }}
        >
            {decor}
            <div ref={innerRef} className="relative flex min-h-full flex-col">{children}</div>
            {frame}
        </div>
    );
}

// Convert a #hex colour to an rgba() string (for translucent glows).
function hexToRgba(hex, a) {
    const h = String(hex || '#000').replace('#', '');
    const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(n.slice(0, 2), 16) || 0;
    const g = parseInt(n.slice(2, 4), 16) || 0;
    const b = parseInt(n.slice(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Subtle tiling sparkle/diamond motif — pure CSS, renders in html-to-image too.
const SPARKLE_PATTERN =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='46' height='46'%3E%3Cpath d='M23 6l2.4 14.6L40 23l-14.6 2.4L23 40l-2.4-14.6L6 23l14.6-2.4z' fill='%23ffffff' fill-opacity='0.05'/%3E%3C/svg%3E\")";

/**
 * PosterBase — the shared, theme-driven biggapon poster. Every template is this
 * component with a different `theme` (see themes.js). `theme.layout` chooses the
 * structural layout: 'classic', 'featured', or 'banner'. Renders live (correct
 * Bengali) and includes the shop footer, so it can be captured to a JPG. Colours
 * come from `theme` via inline styles; sizes use container-query units, each
 * multiplied by the `--fs` auto-fit scale (see usePosterFit) so the content
 * always fills the fixed A4 sheet exactly — no dead space when printed.
 */

function LogoCircle({ job, size = 14 }) {
    return (
        <div
            className="flex flex-none items-center justify-center"
            style={{ height: `calc(${size}cqw * var(--fs, 1))`, width: `calc(${size}cqw * var(--fs, 1))` }}
        >
            <img src={jobLogoSrc(job)} alt="" className="h-full w-full object-contain" />
        </div>
    );
}

function Badge({ theme, label }) {
    // Compact stacked badge (icon over wrapped text) so it stays narrow and
    // doesn't push the centered title off-centre.
    return (
        <div
            className="flex-none rotate-[-5deg] rounded-[1.5cqw] px-[1.8cqw] py-[1.4cqw] text-center ring-[0.3cqw]"
            style={{ background: theme.badgeBg, color: theme.badgeText, '--tw-ring-color': theme.badgeRing, maxWidth: 'calc(17cqw * var(--fs, 1))' }}
        >
            <div className="flex flex-col items-center gap-[0.4cqw] text-[calc(2.4cqw*var(--fs,1))] font-black leading-[1.05]">
                <IconSpeakerphone className="h-[calc(3cqw*var(--fs,1))] w-[calc(3cqw*var(--fs,1))] flex-none" stroke={2} />
                {/* size class must live on the span itself — a global `span{font-size:12px}`
                    rule otherwise overrides the parent's cqw size, blowing up tiny previews */}
                <span className="text-[calc(2.4cqw*var(--fs,1))]">{label}</span>
            </div>
        </div>
    );
}

// Long box values are never ellipsized — instead the font shrinks (down to a
// floor) as the text grows, so the whole value stays visible inside its box.
// `comfy` = the length that still renders at full size.
function valueFitScale(text, comfy = 18) {
    const len = String(text || '').length;
    if (len <= comfy) return 1;
    return Math.max(0.62, Math.sqrt(comfy / len));
}

function InfoBox({ Icon, label, value, theme, compact = false }) {
    const scale = valueFitScale(value, compact ? 14 : 18);
    return (
        <div
            className={`flex flex-col items-center justify-center overflow-hidden rounded-[2.2cqw] border-[0.3cqw] text-center shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.18)] ${compact ? 'p-[calc(1.4cqw*var(--fs,1))]' : 'p-[calc(2cqw*var(--fs,1))]'}`}
            style={{ background: theme.boxBg, borderColor: theme.boxBorder }}
        >
            <div className={`font-bold ${compact ? 'text-[calc(2cqw*var(--fs,1))]' : 'text-[calc(2.3cqw*var(--fs,1))]'}`} style={{ color: theme.boxLabel }}>{label}:</div>
            <div className="mt-[0.6cqw] flex items-center justify-center">
                {/* inline fontSize (not a class) also outranks the global span{font-size:12px} rule */}
                <span
                    className="font-extrabold leading-tight"
                    style={{ color: theme.boxValue, fontSize: `calc(${compact ? 2.6 : 3.2}cqw * var(--fs, 1) * ${scale})` }}
                >
                    {value}
                </span>
            </div>
        </div>
    );
}

export default function PosterBase({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    // Auto-fit: tunes the `--fs` font-scale var so the content exactly fills
    // the fixed A4 sheet (long content shrinks, short content grows).
    const { boxRef, innerRef, contentRef } = usePosterFit([job, business, theme, showFooter, t]);
    if (!job || !theme) return null;

    // Poster education & age come exclusively from the dedicated poster fields
    // (set on the job in the admin form) — not from eligibility/vacancy data.
    // (a job with none shows "প্রযোজ্য নয়" instead of a dash)
    const edu = posterTextOrNA(job.posterQualification, t);
    const age = posterTextOrNA(job.posterBosoyShima, t);
    const vacancyStr = toBnDigits(job.totalVacancies || 0);

    const shopName = business?.shopName?.trim();
    const address = business?.businessAddress?.trim();
    const phone = business?.phone?.trim();
    const shopLogo = business?.shopLogo?.trim();

    const layout = theme.layout || 'classic';

    /* ---------- Header ---------- */
    const header =
        layout === 'banner' ? (
            <div
                className="relative flex flex-col items-center rounded-[2.5cqw] px-[4cqw] pb-[2.5cqw] pt-[2cqw] text-center"
                style={{ background: 'rgba(0,0,0,0.24)', border: `0.3cqw solid ${theme.frame}` }}
            >
                <LogoCircle job={job} theme={theme} size={12} />
                <div
                    className="mt-[1cqw] max-w-[82%] text-[calc(7cqw*var(--fs,1))] font-extrabold leading-[1.08] [text-shadow:0_0.3cqw_0.6cqw_rgba(0,0,0,0.45)]"
                    style={{ color: theme.title }}
                >
                    {job.organization}
                </div>
            </div>
        ) : (
            // Title truly centred on the poster; logo + badge sit in the corners.
            <div className="relative flex min-h-[calc(15cqw*var(--fs,1))] items-center justify-center px-[calc(18cqw*var(--fs,1))] text-center">
                <div
                    className="text-[calc(7cqw*var(--fs,1))] font-extrabold leading-[1.1] [text-shadow:0_0.3cqw_0.6cqw_rgba(0,0,0,0.45)]"
                    style={{ color: theme.title }}
                >
                    {job.organization}
                </div>
                <div className="absolute left-[0.4cqw] -top-[3cqw]">
                    <LogoCircle job={job} theme={theme} size={14} />
                </div>
            </div>
        );

    /* ---------- Date pills ---------- */
    // `compact` keeps both pills on a single line (landscape's narrow left column
    // wraps them otherwise, which pushes the footer out of the fixed-ratio sheet).
    const renderDates = (compact = false) => (
        <div className={`flex justify-center ${compact ? 'flex-nowrap gap-[calc(1.5cqw*var(--fs,1))]' : 'flex-wrap gap-[calc(2cqw*var(--fs,1))]'}`}>
            {[
                { l: t('biggapon.startLabel'), v: formatBnDate(job.publishedDate) },
                { l: t('biggapon.endLabel'), v: formatBnDate(job.deadlineDate) },
            ].map((d, i) => (
                <div
                    key={i}
                    className={`flex items-center whitespace-nowrap rounded-full shadow-[0_0.4cqw_1cqw_rgba(0,0,0,0.28)] ${compact ? 'gap-[1cqw] px-[1.8cqw] py-[calc(1cqw*var(--fs,1))]' : 'gap-[calc(1.5cqw*var(--fs,1))] px-[2.5cqw] py-[calc(1.2cqw*var(--fs,1))]'}`}
                    style={{ background: theme.pillBg }}
                >
                    <IconCalendarEvent className={`flex-none ${compact ? 'h-[calc(2.4cqw*var(--fs,1))] w-[calc(2.4cqw*var(--fs,1))]' : 'h-[calc(3cqw*var(--fs,1))] w-[calc(3cqw*var(--fs,1))]'}`} style={{ color: theme.pillValueBg }} stroke={2} />
                    <span className={`font-semibold ${compact ? 'text-[calc(2.3cqw*var(--fs,1))]' : 'text-[calc(2.9cqw*var(--fs,1))]'}`} style={{ color: theme.pillLabel }}>{d.l}:</span>
                    <span
                        className={`whitespace-nowrap rounded-full font-bold ${compact ? 'px-[1.4cqw] py-[0.3cqw] text-[calc(2.3cqw*var(--fs,1))]' : 'px-[2cqw] py-[0.3cqw] text-[calc(2.9cqw*var(--fs,1))]'}`}
                        style={{ background: theme.pillValueBg, color: theme.pillValueText }}
                    >
                        {d.v}
                    </span>
                </div>
            ))}
        </div>
    );

    /* ---------- Boxes ---------- */
    let boxes;
    if (layout === 'featured') {
        // Big vacancy hero panel, then education + age side by side.
        boxes = (
            <>
                <div
                    className="flex items-center justify-center gap-[3cqw] rounded-[2.5cqw] border-[0.4cqw] px-[3cqw] py-[calc(1.5cqw*var(--fs,1))] shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.22)]"
                    style={{ background: theme.vacancyBg, borderColor: theme.vacancyBorder }}
                >
                    <IconUsersGroup className="h-[calc(8cqw*var(--fs,1))] w-[calc(8cqw*var(--fs,1))] flex-none" style={{ color: theme.vacancyNum }} stroke={1.7} />
                    <div className="text-center leading-none">
                        <div className="text-[calc(2.6cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyLabel')}:</div>
                        <div className="text-[calc(12cqw*var(--fs,1))] font-black" style={{ color: theme.vacancyNum }}>
                            {vacancyStr}
                            <span className="pl-[1cqw] text-[calc(3.6cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>
                                {t('biggapon.vacancyUnit')}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-[2.2cqw]">
                    <InfoBox Icon={IconBooks} label={t('biggapon.eduLabel')} value={edu} theme={theme} />
                    <InfoBox Icon={IconUser} label={t('biggapon.ageLabel')} value={age} theme={theme} />
                </div>
            </>
        );
    } else if (layout === 'banner') {
        // One divided panel with three columns.
        boxes = (
            <div
                className="grid grid-cols-3 overflow-hidden rounded-[2.5cqw] border-[0.3cqw] shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.2)]"
                style={{ background: theme.boxBg, borderColor: theme.boxBorder }}
            >
                <div className="p-[calc(2cqw*var(--fs,1))] text-center" style={{ borderRight: `0.25cqw solid ${theme.boxBorder}` }}>
                    <div className="text-[calc(2.2cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.eduLabel')}:</div>
                    <div className="mt-[0.6cqw] font-extrabold leading-tight" style={{ color: theme.boxValue, fontSize: `calc(3cqw * var(--fs, 1) * ${valueFitScale(edu, 16)})` }}>{edu}</div>
                </div>
                <div className="p-[calc(2cqw*var(--fs,1))] text-center" style={{ borderRight: `0.25cqw solid ${theme.boxBorder}` }}>
                    <div className="text-[calc(2.2cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyLabel')}:</div>
                    <div className="text-[calc(8cqw*var(--fs,1))] font-black leading-none" style={{ color: theme.vacancyNum }}>
                        {vacancyStr}
                        <span className="text-[calc(3cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}> {t('biggapon.vacancyUnit')}</span>
                    </div>
                </div>
                <div className="p-[calc(2cqw*var(--fs,1))] text-center">
                    <div className="text-[calc(2.2cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.ageLabel')}:</div>
                    <div className="mt-[0.6cqw] font-extrabold leading-tight" style={{ color: theme.boxValue, fontSize: `calc(3cqw * var(--fs, 1) * ${valueFitScale(age, 16)})` }}>{age}</div>
                </div>
            </div>
        );
    } else {
        // classic — three separate cards, vacancy in the middle.
        boxes = (
            <div className="grid grid-cols-[1fr_1.25fr_1fr] items-stretch gap-[2.2cqw]">
                <InfoBox Icon={IconBooks} label={t('biggapon.eduLabel')} value={edu} theme={theme} />
                <div
                    className="flex flex-col items-center justify-center rounded-[2.2cqw] border-[0.4cqw] p-[calc(1.5cqw*var(--fs,1))] text-center shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.22)]"
                    style={{ background: theme.vacancyBg, borderColor: theme.vacancyBorder }}
                >
                    <div className="text-[calc(2.6cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyLabel')}:</div>
                    <div className="flex items-center justify-center gap-[calc(1.5cqw*var(--fs,1))] leading-none">
                        <IconUsersGroup className="h-[calc(6cqw*var(--fs,1))] w-[calc(6cqw*var(--fs,1))] flex-none" style={{ color: theme.vacancyNum }} stroke={1.8} />
                        <span className="text-[calc(9.5cqw*var(--fs,1))] font-black" style={{ color: theme.vacancyNum }}>{vacancyStr}</span>
                        <span className="pb-[1.5cqw] text-[calc(3.4cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyUnit')}</span>
                    </div>
                </div>
                <InfoBox Icon={IconUser} label={t('biggapon.ageLabel')} value={age} theme={theme} />
            </div>
        );
    }

    const isLandscape = theme.orientation === 'landscape';

    /* ---------- Promo line (single line) ---------- */
    const promo = (
        <div className="text-center leading-tight py-[calc(1.6cqw*var(--fs,1))]">
            <div className="text-[calc(2.5cqw*var(--fs,1))] font-semibold" style={{ color: theme.promo1 }}>✓ {t('biggapon.promoLine1')}</div>
        </div>
    );

    /* ---------- Contact bar ---------- */
    const renderContact = (compact = false) => (
        <div
            className={`rounded-[1.8cqw] text-center font-bold shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.25)] ring-[0.3cqw] ${compact ? 'py-[calc(1.3cqw*var(--fs,1))] text-[calc(3.4cqw*var(--fs,1))]' : 'py-[calc(1.8cqw*var(--fs,1))] text-[calc(4.6cqw*var(--fs,1))]'}`}
            style={{ background: theme.contactBg, color: theme.contactText, '--tw-ring-color': theme.contactRing }}
        >
            {t('biggapon.contactCta')}
        </div>
    );

    /* ---------- Background decor (glow + sparkle) — sits BEHIND the content ---------- */
    const decor = (
        <>
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background: `radial-gradient(125% 70% at 50% -12%, ${hexToRgba(theme.accent, theme.light ? 0.1 : 0.16)}, transparent 55%), radial-gradient(130% 62% at 50% 114%, ${theme.light ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.32)'}, transparent 55%)`,
                }}
            />
            <div
                className="pointer-events-none absolute inset-0"
                style={{ backgroundImage: SPARKLE_PATTERN, backgroundSize: '46px 46px', opacity: 0.9 }}
            />
        </>
    );

    /* ---------- Double frame — rendered ON TOP so it encloses the footer too ---------- */
    const frame = (
        <>
            <div
                className="pointer-events-none absolute inset-[1.6cqw] rounded-[2cqw] border-[0.4cqw]"
                style={{ borderColor: theme.frame, opacity: 0.5 }}
            />
            <div
                className="pointer-events-none absolute inset-[2.6cqw] rounded-[1.6cqw] border-[0.15cqw]"
                style={{ borderColor: theme.frame, opacity: 0.3 }}
            />
        </>
    );

    /* ---------- The full portrait poster — a fixed A4 (210:297) sheet ---------- */
    const posterPortrait = (
        <div
            ref={boxRef}
            className={`relative w-full overflow-hidden ${className}`}
            style={{ aspectRatio: '210 / 297', containerType: 'inline-size', background: theme.posterBg, '--fs': 1 }}
        >
            {decor}
            <div ref={innerRef} className="relative flex min-h-full flex-col">
                <div ref={contentRef} className={`flex flex-1 flex-col justify-center gap-[calc(2cqw*var(--fs,1))] px-[4cqw] pb-[calc(3cqw*var(--fs,1))] ${layout === 'banner' ? 'pt-[2.5cqw]' : 'pt-[calc(6cqw*var(--fs,1))]'}`}>
                    {header}
                    {job.position && (
                        <div className="text-center text-[calc(3.4cqw*var(--fs,1))] font-semibold" style={{ color: theme.subtitle }}>
                            {job.position}
                        </div>
                    )}
                    {renderDates(false)}
                    {boxes}
                    {promo}
                    {renderContact(false)}
                </div>
                {showFooter && (
                    <div
                        className="w-full border-t px-[4cqw] py-[calc(3cqw*var(--fs,1))] text-center"
                        style={{ background: theme.footerBg, borderColor: theme.footerDivider }}
                    >
                        <div className="flex items-center justify-center gap-[2.5cqw]">
                            {shopLogo && <img src={shopLogo} alt="" className="h-[calc(9cqw*var(--fs,1))] w-[calc(9cqw*var(--fs,1))] flex-none rounded-full object-contain bg-white/95 p-[0.8cqw]" />}
                            <div
                                className="text-[calc(7cqw*var(--fs,1))] font-extrabold leading-tight"
                                style={{ color: theme.footerName, opacity: shopName ? 1 : 0.6 }}
                            >
                                {shopName || t('biggapon.shopNamePlaceholder')}
                            </div>
                        </div>
                        <div
                            className="mt-[0.6cqw] text-[calc(4.6cqw*var(--fs,1))] leading-tight"
                            style={{ color: theme.footerText, opacity: address ? 1 : 0.6 }}
                        >
                            {address || t('biggapon.addressPlaceholder')}
                        </div>
                        {phone && <div className="mt-[0.6cqw] text-[calc(4.8cqw*var(--fs,1))] font-bold" style={{ color: theme.footerText }}>{phone}</div>}
                    </div>
                )}
            </div>
            {frame}
            {/* Notice badge is painted AFTER the frame so the decorative border never
                cuts across it — it sits on top of the top-right corner. */}
            <div className="absolute right-[3cqw] top-[1cqw]">
                <Badge theme={theme} label={t('biggapon.noticeTitle')} />
            </div>
        </div>
    );

    /* ---------- Landscape = A4-landscape (297:210) sheet, body fills width ---------- */
    if (isLandscape) {
        return (
            <LandscapeFit bg={theme.posterBg} decor={decor} frame={frame} className={className} boxRef={boxRef} innerRef={innerRef}>
                <>
                    <div ref={contentRef} className="flex flex-1 items-center gap-[3cqw] px-[3.5cqw] py-[calc(2.5cqw*var(--fs,1))]">
                        {/* Left column — header, dates, promo, contact */}
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-[calc(1.6cqw*var(--fs,1))]">
                            <div className="flex items-center gap-[2.5cqw]">
                                <LogoCircle job={job} size={11} />
                                <div className="min-w-0 flex-1 text-left">
                                    <div
                                        className="text-[calc(4.8cqw*var(--fs,1))] font-extrabold leading-[1.1] [text-shadow:0_0.3cqw_0.6cqw_rgba(0,0,0,0.45)]"
                                        style={{ color: theme.title }}
                                    >
                                        {job.organization}
                                    </div>
                                    {job.position && (
                                        <div className="mt-[0.4cqw] text-[calc(2.4cqw*var(--fs,1))] font-semibold" style={{ color: theme.subtitle }}>
                                            {job.position}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {renderDates(true)}
                            {promo}
                            {renderContact(true)}
                        </div>
                        {/* Right column — notice badge (top-right), vacancy hero, education + age */}
                        <div className="flex w-[36%] flex-none flex-col justify-center gap-[calc(1.3cqw*var(--fs,1))]">
                            <div className="self-end">
                                <Badge theme={theme} label={t('biggapon.noticeTitle')} />
                            </div>
                            <div
                                className="flex items-center justify-center gap-[1.8cqw] rounded-[2.2cqw] border-[0.4cqw] px-[2cqw] py-[calc(1.2cqw*var(--fs,1))] shadow-[0_0.6cqw_1.6cqw_rgba(0,0,0,0.22)]"
                                style={{ background: theme.vacancyBg, borderColor: theme.vacancyBorder }}
                            >
                                <IconUsersGroup className="h-[calc(5.5cqw*var(--fs,1))] w-[calc(5.5cqw*var(--fs,1))] flex-none" style={{ color: theme.vacancyNum }} stroke={1.7} />
                                <div className="text-center leading-none">
                                    <div className="text-[calc(2cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyLabel')}:</div>
                                    <div className="text-[calc(6.8cqw*var(--fs,1))] font-black" style={{ color: theme.vacancyNum }}>
                                        {vacancyStr}
                                        <span className="pl-[0.8cqw] text-[calc(2.4cqw*var(--fs,1))] font-bold" style={{ color: theme.boxLabel }}>{t('biggapon.vacancyUnit')}</span>
                                    </div>
                                </div>
                            </div>
                            <InfoBox Icon={IconBooks} label={t('biggapon.eduLabel')} value={edu} theme={theme} compact />
                            <InfoBox Icon={IconUser} label={t('biggapon.ageLabel')} value={age} theme={theme} compact />
                        </div>
                    </div>
                    {/* Footer — smaller type tuned for the wide format */}
                    {showFooter && (
                        <div
                            className="w-full border-t px-[4cqw] py-[calc(2.2cqw*var(--fs,1))] text-center"
                            style={{ background: theme.footerBg, borderColor: theme.footerDivider }}
                        >
                            <div className="flex items-center justify-center gap-[calc(1.5cqw*var(--fs,1))]">
                                {shopLogo && <img src={shopLogo} alt="" className="h-[calc(5.5cqw*var(--fs,1))] w-[calc(5.5cqw*var(--fs,1))] flex-none rounded-full object-contain bg-white/95 p-[0.5cqw]" />}
                                <div className="text-[calc(4.2cqw*var(--fs,1))] font-extrabold leading-tight" style={{ color: theme.footerName, opacity: shopName ? 1 : 0.6 }}>
                                    {shopName || t('biggapon.shopNamePlaceholder')}
                                </div>
                            </div>
                            <div className="mt-[0.4cqw] text-[calc(2.7cqw*var(--fs,1))] leading-tight" style={{ color: theme.footerText, opacity: address ? 1 : 0.6 }}>
                                {address || t('biggapon.addressPlaceholder')}
                            </div>
                            {phone && <div className="mt-[0.4cqw] text-[calc(2.9cqw*var(--fs,1))] font-bold" style={{ color: theme.footerText }}>{phone}</div>}
                        </div>
                    )}
                </>
            </LandscapeFit>
        );
    }

    return posterPortrait;
}
